import { useSeoMeta, useHead } from '@unhead/react';
import {ArrowRight, CheckCircle2, Compass, MapPinned, Shield, Truck} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { SiteShell } from '@/components/SiteShell';
import { normalizeCanonicalUrl } from '@/lib/seo';
import defaultBg from '../../images/VIP House Movers Removal Van.jpg';

function getAreaSearchTerms(areaLabel: string): string[] {
  return [
    `house removals ${areaLabel}`,
    `removals company ${areaLabel}`,
    `man and van ${areaLabel}`,
    `packing services ${areaLabel}`,
    `storage solutions ${areaLabel}`,
    `moving company ${areaLabel}`,
    `cheap removals ${areaLabel}`,
    `local movers ${areaLabel}`,
  ];
}

type AreaDetailPageProps = {
  activePath: string;
  areaLabel: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  movingSupport: string[];
  idealFor: string[];
  seoPoints?: string[];
  faqs?: Array<{ question: string; answer: string }>;
  backgroundImage?: string;
};

export function AreaDetailPage({
  activePath,
  areaLabel,
  title,
  description,
  intro,
  highlights,
  movingSupport,
  idealFor,
  seoPoints = [],
  faqs = [],
  backgroundImage,
}: AreaDetailPageProps) {
  const searchTerms = getAreaSearchTerms(areaLabel);

  useSeoMeta({
    title: `House Removals ${areaLabel} | VIP House Movers`,
    description: `Trusted house removals, man and van service and local moving company support in ${areaLabel}. Eco-friendly, fully insured and 5-star rated. Call 0121 752 7107 for a free quote.`,
    ogTitle: `House Removals ${areaLabel} | VIP House Movers`,
    ogDescription: `Trusted house removals, man and van service and local moving company support in ${areaLabel}. Eco-friendly, fully insured and 5-star rated. Call 0121 752 7107 for a free quote.`,
    ogImage: backgroundImage ?? 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: `https://www.viphousemovers.com${activePath}`,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: `House Removals ${areaLabel} | VIP House Movers`,
    twitterDescription: `Trusted house removals, man and van service and local moving company support in ${areaLabel}. Eco-friendly, fully insured and 5-star rated.`,
    twitterImage: backgroundImage ?? 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl(`https://www.viphousemovers.com${activePath}`);

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: `${searchTerms.join(', ')}, local moving company near me, trusted removals company`,
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': `https://www.viphousemovers.com${activePath}#local-business`,
          name: `VIP House Movers - ${areaLabel}`,
          url: `https://www.viphousemovers.com${activePath}`,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.viphousemovers.com${activePath}`,
          },
          telephone: '+44-121-752-7107',
          email: 'info@viphousemovers.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: areaLabel,
            addressRegion: 'West Midlands',
            addressCountry: 'GB',
          },
          areaServed: {
            '@type': 'City',
            name: areaLabel,
          },
          serviceType: [
            'House Removals',
            'Office Removals',
            'Packing Services',
            'Furniture Assembly',
            'Storage Solutions',
            'Man and Van',
          ],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '150',
            bestRating: '5',
            worstRating: '1',
          },
          image: backgroundImage || 'https://www.viphousemovers.com/og-home.jpg',
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Areas We Cover',
              item: 'https://www.viphousemovers.com/areas-we-cover',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: areaLabel,
              item: `https://www.viphousemovers.com${activePath}`,
            },
          ],
        }),
      },
    ],
  });
  return (
    <SiteShell activePath={activePath} fixedNav areaMenuPath={activePath}>
      <PageHero
        eyebrow="Areas We Cover"
        title={title}
        description={description}
        primaryLabel="Get a Free Quote"
        primaryTo="/quote"
        secondaryLabel="Contact VIP House Movers"
        secondaryTo="/contact"
        backgroundImage={backgroundImage ?? defaultBg}
      />

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.3fr_0.7fr] items-start">
            <div className="space-y-8">
              <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                  <MapPinned className="h-4 w-4" />
                  {areaLabel}
                </div>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">Local moving support with VIP-level care</h2>
                <p className="mt-5 text-lg leading-relaxed text-gray-600">
                  {intro}
                </p>
              </div>
              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                  House Removals in {areaLabel}
                </div>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">{areaLabel} house removals with local expertise</h2>
                <p className="mt-5 text-base leading-7 text-gray-600">
                  VIP House Movers delivers {areaLabel} house removals, flat moves, and local home relocations with the right vehicle, crew size, and protective packing for every property. Our team manages city access, narrow drives, and gated or apartment move challenges so your move is more reliable.
                </p>
                <p className="mt-5 text-base leading-7 text-gray-600">
                  If you are looking for house removals {areaLabel} or removals in {areaLabel}, this page is designed to show real local moving support, trusted customer care, and the practical experience we bring to every home move.
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {highlights.map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37]/10">
                      <CheckCircle2 className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <p className="text-base font-medium leading-7 text-gray-700">{item}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                  Local search phrases
                </div>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">What people in {areaLabel} search for</h2>
                <p className="mt-5 text-base leading-7 text-gray-600">
                  These are the common queries that match local removals and moving services in {areaLabel}. Including them on this page helps search engines connect your service to real customer intent.
                </p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {searchTerms.map((term) => (
                    <div key={term} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-gray-700">
                      {term}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl bg-gray-900 p-8 text-white shadow-xl">
                <h2 className="text-3xl font-bold tracking-tight">How VIP House Movers supports moves in {areaLabel}</h2>
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  {movingSupport.map((step, index) => (
                    <div key={step} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                      <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AF37] text-sm font-bold text-gray-900">
                        {index + 1}
                      </div>
                      <p className="text-base leading-7 text-gray-200">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {seoPoints.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                    Local SEO Signals
                  </div>
                  <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">Why customers in {areaLabel} choose VIP House Movers</h2>
                  <div className="mt-8 grid gap-4 md:grid-cols-2">
                    {seoPoints.map((point) => (
                      <div key={point} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                        <div className="flex items-start gap-3 text-gray-700">
                          <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
                          <span className="leading-7">{point}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {faqs.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900">Frequently asked questions for {areaLabel} removals</h2>
                  <div className="mt-8 space-y-4">
                    {faqs.map((faq) => (
                      <details key={faq.question} className="group rounded-2xl border border-gray-200 bg-gray-50 p-5 hover:border-[#D4AF37]/40 transition-colors">
                        <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-lg font-semibold text-gray-900">
                          <span>{faq.question}</span>
                          <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37] transition-transform group-open:rotate-90" />
                        </summary>
                        <p className="mt-4 leading-7 text-gray-600">{faq.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-6 lg:sticky lg:top-32">
              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900">Ideal for</h3>
                <ul className="mt-6 space-y-4">
                  {idealFor.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-gray-600">
                      <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
                      <span className="leading-7">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-8 shadow-sm">
                <div className="flex items-center gap-3 text-gray-900">
                  <Shield className="h-6 w-6 text-[#D4AF37]" />
                  <h3 className="text-xl font-bold">Why choose VIP House Movers?</h3>
                </div>
                <div className="mt-6 space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <Truck className="h-5 w-5 text-[#D4AF37]" />
                    <span>Premium house and business moving support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Compass className="h-5 w-5 text-[#D4AF37]" />
                    <span>Flexible coverage for local and regional relocations</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#D4AF37]" />
                    <span>Careful handling backed by dependable communication</span>
                  </div>
                </div>
                <div className="mt-8 flex flex-col gap-3">
                  <Link
                    to="/quote"
                    className="inline-flex items-center justify-center rounded-xl bg-[#D4AF37] px-6 py-3 text-base font-bold text-gray-900 transition-all duration-300 hover:scale-[1.02] hover:bg-[#c79f2e]"
                  >
                    Request Your Quote
                  </Link>
                  <Link
                    to="/areas-we-cover"
                    className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-6 py-3 text-base font-bold text-gray-800 transition-colors hover:border-[#D4AF37] hover:text-[#B78D19]"
                  >
                    View All Areas
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

